import { Controller } from "@hotwired/stimulus"
import { StringUtils } from "../utils/string_utils"

/**
 * Slash Command Controller
 *
 * Shows a filterable dropdown of specialists when the user types "/" in the
 * assistant textarea. Selecting a specialist displays it as a pill and sets
 * the hidden specialist_id field.
 *
 * Values:
 * - specialists: Object with { specialists: [{ name, slug, tooltip_url }],
 *                manage_url?, manage_label?, empty_message?, discover_url?, discover_label? }
 * - enabled: Boolean (default true) — disables slash detection when false
 * - manageIcon: String — SVG markup for the manage link icon
 * - pillTooltipReference: String — selector of the ancestor the pill tooltip is
 *                anchored to (width + position); defaults to the closest form
 *
 * Targets:
 * - input: the textarea/input
 * - menu: dropdown container
 * - pill: pill container
 * - specialistId: hidden input for specialist_id
 */
export default class extends Controller {
  static targets = ["input", "menu", "pill", "specialistId"]
  static values = {
    specialists: Object,
    enabled: { type: Boolean, default: true },
    manageIcon: String,
    labelGobierto: { type: String, default: "Gobierto" },
    pillTooltipReference: { type: String, default: "" }
  }

  connect() {
    this._activeIndex = -1
    this._menuVisible = false
    this._boundClickOutside = this._clickOutside.bind(this)
    this._boundOnSubmitEnd = this._onSubmitEnd.bind(this)
    this.element.closest("form")?.addEventListener("turbo:submit-end", this._boundOnSubmitEnd)
    this._restorePreselectedSpecialist()
  }

  // When the input renders with a specialist already set (e.g. editing a saved
  // automation, or re-rendering a form after a validation error), rebuild the
  // pill so the selection is visible instead of only living in the hidden field.
  _restorePreselectedSpecialist() {
    if (!this.hasSpecialistIdTarget || !this.specialistIdTarget.value) return

    const specialists = this.specialistsValue.specialists || []
    const specialist = specialists.find(s => s.slug === this.specialistIdTarget.value)
    if (!specialist) return

    this._renderPill(specialist)
    this.inputTarget?.removeAttribute("required")
  }

  disconnect() {
    document.removeEventListener("click", this._boundClickOutside)
    this.element.closest("form")?.removeEventListener("turbo:submit-end", this._boundOnSubmitEnd)
  }

  onInput() {
    // Don't show menu if disabled or a specialist is already selected
    if (!this.enabledValue) return
    if (this.specialistIdTarget.value) return

    const value = this.inputTarget.value
    const cursorPos = this.inputTarget.selectionStart

    // Find the slash command being typed: look backwards from cursor for "/"
    const textBeforeCursor = value.slice(0, cursorPos)
    const slashIndex = textBeforeCursor.lastIndexOf("/")

    // Show menu only if "/" is at the start of the text or preceded by whitespace/newline
    if (slashIndex === -1) {
      this._hideMenu()
      return
    }

    const charBefore = slashIndex > 0 ? textBeforeCursor[slashIndex - 1] : null
    if (charBefore !== null && charBefore !== " " && charBefore !== "\n") {
      this._hideMenu()
      return
    }

    const query = textBeforeCursor.slice(slashIndex + 1).toLowerCase()
    this._slashIndex = slashIndex
    this._showMenu(query)
  }

  onKeydown(event) {
    // Remove specialist on Backspace when textarea is empty
    if (event.key === "Backspace" && this.specialistIdTarget.value && !this.inputTarget.value) {
      this.removeSpecialist()
      return
    }

    if (!this._menuVisible) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        event.stopImmediatePropagation()
        this._moveSelection(1)
        break
      case "ArrowUp":
        event.preventDefault()
        event.stopImmediatePropagation()
        this._moveSelection(-1)
        break
      case "Enter":
        event.preventDefault()
        event.stopImmediatePropagation()
        this._selectCurrent()
        break
      case "Escape":
        event.preventDefault()
        event.stopImmediatePropagation()
        this._hideMenu()
        break
      case "Tab":
        event.preventDefault()
        event.stopImmediatePropagation()
        this._selectCurrent()
        break
    }
  }

  selectSpecialist(event) {
    const slug = event.currentTarget.dataset.slug
    const specialists = this.specialistsValue.specialists || []
    const specialist = specialists.find(s => s.slug === slug)
    if (specialist) {
      this._applySpecialist(specialist)
    }
  }

  // Select a specialist from outside the slash flow (e.g. plus-menu, omni tab).
  // Clears the input so _applySpecialist always fills it with the sample_question,
  // even if the user had typed something.
  selectSpecialistFromMenu(event) {
    event.preventDefault()
    const slug = event.currentTarget.dataset.slug
    const specialists = this.specialistsValue.specialists || []
    const specialist = specialists.find(s => s.slug === slug)
    if (!specialist) return

    this.inputTarget.value = ""
    this._slashIndex = 0
    this._applySpecialist(specialist)
  }

  insertSlash(event) {
    event.stopPropagation()
    if (this.specialistIdTarget.value) return // already has a specialist

    const input = this.inputTarget
    const value = input.value
    // Insert "/" at cursor position (or at start if empty)
    const pos = input.selectionStart || 0
    const needsSpace = pos > 0 && value[pos - 1] !== " " && value[pos - 1] !== "\n"
    const slash = (needsSpace ? " " : "") + "/"
    input.value = value.slice(0, pos) + slash + value.slice(pos)
    const newPos = pos + slash.length
    input.selectionStart = input.selectionEnd = newPos
    input.focus()

    // Show menu directly on next tick so focus and clickOutside settle first
    requestAnimationFrame(() => {
      this._slashIndex = newPos - 1
      this._showMenu("")
    })
  }

  removeSpecialist() {
    this.specialistIdTarget.value = ""
    this.pillTarget.innerHTML = ""
    this.pillTarget.classList.add("hidden")
    this.pillTarget.classList.remove("flex")
    this.inputTarget.setAttribute("required", "")
    this.inputTarget.focus()
  }

  _onSubmitEnd() {
    if (this.specialistIdTarget.value) {
      this.removeSpecialist()
    }
  }

  // Private

  _showMenu(query) {
    const data = this.specialistsValue
    const filtered = this._filterSpecialists(query)

    // Show blank state if no specialists exist at all
    if (data.specialists.length === 0 && data.empty_message) {
      this._activeIndex = -1
      this._filteredSpecialists = []

      let html = `<div class="slash-command-menu-empty">${this._escapeHtml(data.empty_message)}`
      if (data.discover_url) {
        html += ` <a href="${this._escapeHtml(data.discover_url)}" target="_blank" class="text-blue underline">${this._escapeHtml(data.discover_label)}</a>.`
      }
      html += `</div>`

      this.menuTarget.innerHTML = html
      this._openMenu()
      return
    }

    if (filtered.length === 0) {
      this._hideMenu()
      return
    }

    this._activeIndex = 0
    this._filteredSpecialists = filtered

    let html = ""

    // "Gestionar" link at the top if user has permission
    if (data.manage_url) {
      const icon = this.manageIconValue || ""
      html += `<a href="${this._escapeHtml(data.manage_url)}" target="_blank" class="slash-command-menu-manage">${icon}${this._escapeHtml(data.manage_label)}</a>`
    }

    const gobiertoLabel = this._escapeHtml(this.labelGobiertoValue)
    html += filtered.map((specialist, index) => {
      const chip = specialist.from_gobierto
        ? `<span class="specialist-source-chip">${gobiertoLabel}</span>`
        : ""
      return `
        <div class="slash-command-menu-item ${index === 0 ? 'active' : ''}"
             data-slug="${this._escapeHtml(specialist.slug)}"
             data-action="click->slash-command#selectSpecialist">
          <span class="slash-command-menu-item-left">
            ${chip}
            <span class="slash-command-menu-item-name">${this._escapeHtml(specialist.name)}</span>
          </span>
          <span class="slash-command-menu-item-slug">/${this._escapeHtml(specialist.slug)}</span>
        </div>
      `
    }).join("")

    this.menuTarget.innerHTML = html
    this._openMenu()
  }

  _openMenu() {
    this.menuTarget.classList.add("slash-command-menu--open")
    this._menuVisible = true
    document.addEventListener("click", this._boundClickOutside)
  }

  _hideMenu() {
    if (!this._menuVisible) return

    this.menuTarget.classList.remove("slash-command-menu--open")
    // Clear contents after the CSS transition ends
    this.menuTarget.addEventListener("transitionend", () => {
      if (!this._menuVisible) this.menuTarget.innerHTML = ""
    }, { once: true })
    this._menuVisible = false
    this._activeIndex = -1
    this._filteredSpecialists = []
    document.removeEventListener("click", this._boundClickOutside)
  }

  _filterSpecialists(query) {
    const specialists = this.specialistsValue.specialists || []
    if (!query) return specialists

    return specialists.filter(s => {
      const nameMatch = s.name.toLowerCase().includes(query)
      const slugMatch = s.slug.toLowerCase().includes(query)
      return nameMatch || slugMatch
    })
  }

  _moveSelection(direction) {
    const items = this.menuTarget.querySelectorAll(".slash-command-menu-item")
    if (items.length === 0) return

    items[this._activeIndex]?.classList.remove("active")

    this._activeIndex += direction
    if (this._activeIndex < 0) this._activeIndex = items.length - 1
    if (this._activeIndex >= items.length) this._activeIndex = 0

    items[this._activeIndex]?.classList.add("active")
    items[this._activeIndex]?.scrollIntoView({ block: "nearest" })
  }

  _selectCurrent() {
    if (this._activeIndex < 0 || !this._filteredSpecialists?.length) {
      this._hideMenu()
      return
    }

    const specialist = this._filteredSpecialists[this._activeIndex]
    if (specialist) {
      this._applySpecialist(specialist)
    }
  }

  _applySpecialist(specialist) {
    // Remove the slash command text from textarea
    const value = this.inputTarget.value
    const before = value.slice(0, this._slashIndex)
    const afterCursor = value.slice(this.inputTarget.selectionStart)
    const remainingText = (before + afterCursor).trim()

    // Populate with the specialist's sample question if the input would be empty
    const sampleText = specialist.sample_question || ""
    if (remainingText) {
      this.inputTarget.value = remainingText
    } else {
      const { finalText, selection } = StringUtils.parseHighlighted(sampleText)
      this.inputTarget.value = finalText
      this._pendingSelection = selection
    }

    // Set the hidden specialist_id field
    this.specialistIdTarget.value = specialist.slug

    // Show pill
    this._renderPill(specialist)

    // Allow submit without text when specialist is selected
    this.inputTarget.removeAttribute("required")

    // Hide menu and focus textarea
    this._hideMenu()
    this.inputTarget.focus()

    // Select the highlighted segment, or fall back to selecting all text
    if (!remainingText && this.inputTarget.value) {
      const sel = this._pendingSelection
      if (sel) {
        this.inputTarget.setSelectionRange(sel.start, sel.end)
      } else {
        this.inputTarget.setSelectionRange(0, this.inputTarget.value.length)
      }
      this._pendingSelection = null
    }

    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  _renderPill(specialist) {
    const pill = document.createElement("span")
    pill.className = "slash-command-pill"

    const text = document.createElement("span")
    text.className = "slash-command-pill-text"
    text.textContent = `/${specialist.slug}`

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "slash-command-pill-remove"
    remove.dataset.action = "click->slash-command#removeSpecialist"
    remove.setAttribute("aria-label", I18n.slash_command.remove_specialist_label)
    remove.innerHTML = "&times;"

    pill.appendChild(text)
    pill.appendChild(remove)

    // Add tooltip trigger on the pill span; use the textarea container for positioning so tooltip centers
    if (specialist.tooltip_url) {
      pill.dataset.floatingTooltipTarget = "source"
      pill.dataset.tooltipUrl = specialist.tooltip_url
      pill.dataset.longContent = "true"
      const reference = this._pillTooltipReference()
      if (reference) {
        if (!reference.id) reference.id = `slash-command-reference-${Date.now()}`
        pill.dataset.tooltipReferenceSelector = `#${reference.id}`
      }
    }

    this.pillTarget.innerHTML = ""
    this.pillTarget.appendChild(pill)
    this.pillTarget.classList.remove("hidden")
    this.pillTarget.classList.add("flex")
  }

  _pillTooltipReference() {
    const selector = this.pillTooltipReferenceValue
    return (selector && this.inputTarget.closest(selector)) || this.inputTarget.closest("form")
  }

  _clickOutside(event) {
    if (!this.menuTarget.contains(event.target) && event.target !== this.inputTarget) {
      this._hideMenu()
    }
  }

  _escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

}
